import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { setupErrorInterceptor } from './apiErrorInterceptor';
import { installClientTiming } from './clientTiming';
import tokenStorage from './tokenStorage';
import { generateTraceId } from './trace';

/**
 * Shared API client for the web and React Native apps.
 *
 * The base URL is intentionally NOT read from any bundler-specific env here, so
 * this module stays platform-agnostic. Each host configures it once at startup
 * via `configureApiClient`:
 *   - Web injects `runtimeEnv.getApiUrl()` (relative '' by default → proxied by
 *     the frontend server; absolute if VITE_API_URL is set).
 *   - React Native injects the absolute API URL from its Expo env (no proxy on
 *     device).
 */

export interface ApiClientConfig {
  baseURL?: string;
  withCredentials?: boolean;
  timeout?: number;
  /**
   * Client platform ("web" | "mobile"). Sent as X-Client-Platform on every
   * request so the backend can scope JWT sessions per platform (one web + one
   * mobile session per user). Defaults to "web" when unset.
   */
  platform?: string;
  /**
   * Build identifiers for the running client, reported to the backend so the
   * admin panel can see which app/OTA build a user is on (e.g. to confirm an
   * over-the-air update reached them). The mobile host sets these from
   * expo-constants (appVersion) and expo-updates (runtimeVersion/updateId);
   * web leaves them unset.
   */
  appVersion?: string;
  runtimeVersion?: string;
  updateId?: string;
}

// The client platform, attached to every request as X-Client-Platform. Each
// host sets this once via configureApiClient (web → "web", mobile → "mobile").
let clientPlatform = 'web';

// Optional client build identifiers (mobile only), attached as X-Client-Version
// / X-Client-Runtime-Version / X-Client-Update-Id when set.
let clientAppVersion: string | undefined;
let clientRuntimeVersion: string | undefined;
let clientUpdateId: string | undefined;

// Ceiling for multipart uploads (see the FormData branch in the request
// interceptor). Sized for the 50 MB chapter-audio cap on a slow uplink.
export const UPLOAD_TIMEOUT_MS = 300_000;

// Create axios instance. Defaults match the previous web behaviour; the base
// URL is filled in by configureApiClient before any request is made.
export const apiClient = axios.create({
  baseURL: '',
  withCredentials: true,
  // Production p50/p90 for a fast endpoint are 3.27s/7.24s (Rails itself
  // answers in ~3ms — the gap is network/proxy latency). 10s left only ~38%
  // headroom over p90, so a healthy-but-slow response routinely got killed
  // and paid for a full retry on top of the wait it had already done. 20s
  // clears p90 by ~2.75x without chasing the 41.6s outlier max, which still
  // fails fast next to Cloudflare's upstream timeout (~100s).
  timeout: 20_000,
  headers: {
    'Content-Type': 'application/json',
  },
});

/** Configure the shared API client. Call once at app startup. */
export function configureApiClient(config: ApiClientConfig): void {
  if (config.baseURL !== undefined) apiClient.defaults.baseURL = config.baseURL;
  if (config.withCredentials !== undefined) apiClient.defaults.withCredentials = config.withCredentials;
  if (config.timeout !== undefined) apiClient.defaults.timeout = config.timeout;
  if (config.platform !== undefined) clientPlatform = config.platform;
  if (config.appVersion !== undefined) clientAppVersion = config.appVersion;
  if (config.runtimeVersion !== undefined) clientRuntimeVersion = config.runtimeVersion;
  if (config.updateId !== undefined) clientUpdateId = config.updateId;
}

// Setup error logging and user-friendly messages
setupErrorInterceptor(apiClient);

// Real-user latency per API call, reported in aggregate (see clientTiming.ts).
installClientTiming(apiClient);

// Request interceptor - add JWT access token to requests
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // Attach or generate a client trace ID for correlation with backend logs
    if (!config.headers['X-Client-Trace-Id']) {
      config.headers['X-Client-Trace-Id'] = generateTraceId();
    }

    // Identify the client platform so the backend files the JWT under the right
    // per-platform session slot (web vs mobile).
    config.headers['X-Client-Platform'] = clientPlatform;

    // Report the running build (mobile only) so the backend can record which
    // app/OTA version each user is on.
    if (clientAppVersion) config.headers['X-Client-Version'] = clientAppVersion;
    if (clientRuntimeVersion) config.headers['X-Client-Runtime-Version'] = clientRuntimeVersion;
    if (clientUpdateId) config.headers['X-Client-Update-Id'] = clientUpdateId;

    const token = tokenStorage.getAccessToken();

    // Let Axios set multipart boundaries for FormData payloads.
    if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
      delete config.headers['Content-Type'];

      // File uploads stream through Rails to R2, so wall-clock time scales with
      // the file and the client's uplink — the 10 s fail-fast default aborts a
      // perfectly healthy 15 MB upload on any connection under ~12 Mbps up
      // (i.e. most home uplinks, and most phones). Chapter audio is capped at
      // 50 MB, which needs ~3 min at 2.5 Mbps. Applied here rather than at each
      // call site so every present and future multipart POST/PATCH inherits it.
      if (config.timeout === undefined || config.timeout === apiClient.defaults.timeout) {
        config.timeout = UPLOAD_TIMEOUT_MS;
      }
    }

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor - extract JWT from response headers and handle 401s
apiClient.interceptors.response.use(
  (response) => {
    // Devise JWT sends the token in the Authorization header on sign in/sign up
    const authHeader = response.headers['authorization'];
    if (authHeader && authHeader.startsWith('Bearer ')) {
      const token = authHeader.substring(7);
      tokenStorage.setAccessToken(token);
    }
    return response;
  },
  async (error: AxiosError) => {
    // A beta-access 403 means the backend already rotated the user's jti, so
    // the stored token is dead. Clearing it here is what flips hasToken false —
    // without it AuthenticatedLayout waits on a skeleton forever.
    if (
      error.response?.status === 403 &&
      (error.response?.data as { error?: string } | undefined)?.error === 'beta_access_revoked'
    ) {
      tokenStorage.clearTokens();
    }
    if (error.response?.status === 401) {
      const hadToken = !!error.config?.headers?.Authorization;
      const url = error.config?.url || '';

      // Skip token clearing for /api/current_user when no token was sent —
      // a 401 here simply means "not logged in" and is expected.
      if (url.includes('/api/current_user') && !hadToken) {
        return Promise.reject(error);
      }

      // Clear stored token when a token-bearing request is rejected (expired/revoked).
      // Routing back to login is handled by the host app, not here.
      if (hadToken) {
        tokenStorage.clearTokens();
      }
    }
    return Promise.reject(error);
  }
);

export default apiClient;
